import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import api, { healthApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import LegalDialog from './LegalDialog';

/** Footer mit aktueller App-Version + Pflicht-Links (Impressum/Datenschutz/Nutzungsbedingungen).
 *  Klick auf einen Pflicht-Link öffnet ein Pop-up mit dem unter Einstellungen
 *  hinterlegten HTML-Inhalt – der Nutzer bleibt auf der Akademie. */
export function AppFooter() {
    const [info, setInfo] = useState(null);
    const [legalOpen, setLegalOpen] = useState(null);
    const [health, setHealth] = useState(null);
    const { isAdmin } = useAuth();
    const adminUser = isAdmin?.();

    useEffect(() => {
        let active = true;
        api.get('/version')
            .then(r => { if (active) setInfo(r.data || null); })
            .catch(() => { if (active) setInfo(null); });
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (!adminUser) return undefined;
        let active = true;
        const load = () => {
            healthApi.summary()
                .then(r => { if (active) setHealth(r.data || null); })
                .catch(() => { /* ignore */ });
        };
        load();
        const id = setInterval(load, 60000);
        return () => { active = false; clearInterval(id); };
    }, [adminUser]);

    const version = info?.version || '–';
    const build = info?.build || '';
    // Build-Datum (vom Backend als ISO YYYY-MM-DD) für den Footer ins deutsche
    // Format DD.MM.YYYY umwandeln. Greift defensiv: bleibt der Original-String,
    // wenn das Format unerwartet ist.
    const formatBuildDate = (iso) => {
        if (!iso) return '';
        const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
        return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
    };
    const buildLabel = formatBuildDate(build);
    const linkCls = "hover:text-foreground transition-colors py-2 px-1 min-h-[44px] inline-flex items-center";

    const healthBadge = () => {
        if (!adminUser || !health) return null;
        const map = {
            ok: { Icon: CheckCircle2, cls: 'text-emerald-700 bg-emerald-50 border-emerald-200', label: 'System: alles OK' },
            warning: { Icon: AlertTriangle, cls: 'text-amber-800 bg-amber-50 border-amber-200',
                label: `System: ${health.warning_count} Warnung${health.warning_count !== 1 ? 'en' : ''}` },
            error: { Icon: XCircle, cls: 'text-rose-800 bg-rose-50 border-rose-200',
                label: `System: ${health.error_count} Fehler` },
        };
        const meta = map[health.overall] || map.warning;
        const Icon = meta.Icon;
        return (
            <Link
                to="/admin/system-health"
                className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border text-[11px] font-medium transition-colors hover:opacity-90 ${meta.cls}`}
                data-testid="footer-health-badge"
                title="System-Health-Dashboard öffnen"
            >
                <Icon className="h-3 w-3" />
                {meta.label}
            </Link>
        );
    };

    return (
        <footer
            className="mt-12 border-t border-border/60 px-6 py-4 text-xs text-muted-foreground flex flex-col sm:flex-row items-center justify-between flex-wrap gap-2"
            data-testid="app-footer"
        >
            <span>© {new Date().getFullYear()} BGPR-Akademie · Verwaltungssystem</span>
            <nav className="flex items-center gap-x-4 gap-y-1 flex-wrap">
                <button type="button" onClick={() => setLegalOpen('imprint')} className={linkCls} data-testid="footer-link-imprint">Impressum</button>
                <button type="button" onClick={() => setLegalOpen('privacy_policy')} className={linkCls} data-testid="footer-link-privacy">Datenschutzhinweis</button>
                <button type="button" onClick={() => setLegalOpen('terms_of_service')} className={linkCls} data-testid="footer-link-terms">Nutzungsbedingungen</button>
            </nav>
            <div className="flex items-center gap-3 flex-wrap">
                {healthBadge()}
                <span data-testid="app-footer-version">
                    v<span className="font-mono font-semibold text-foreground/80">{version}</span>
                    {build && <span className="ml-2 opacity-70">Build {buildLabel}</span>}
                </span>
            </div>
            <LegalDialog
                type={legalOpen}
                open={!!legalOpen}
                onOpenChange={(v) => !v && setLegalOpen(null)}
            />
        </footer>
    );
}

export default AppFooter;
